import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {Record<string, number>} */
  #pendingUpdates = {};
  /** @type {boolean} */
  #isRequestInFlight = false;
  /** @type {number | null} */
  #debounceTimer = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const allRows = this.#getAllRows();
    const lineItemRow =
      allRows[line - 1] ||
      (event.target instanceof HTMLElement ? event.target.closest('.cart-items__table-row') : null);
    const key = lineItemRow?.dataset?.key;

    if (quantity === 0) {
      return this.onLineItemRemove(key || line, event);
    }

    if (key) {
      this.queueUpdates({ [key]: quantity });
    } else {
      this.updateQuantity({
        line,
        quantity,
        action: 'change',
      });
    }

    if (lineItemRow) {
      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer?.();
    }
  }

  /**
   * Helper to get all cart item rows as an Array
   * @returns {HTMLTableRowElement[]}
   */
  #getAllRows() {
    if (Array.isArray(this.refs.cartItemRows)) {
      return this.refs.cartItemRows;
    }
    return Array.from(this.querySelectorAll('.cart-items__table-row'));
  }

  /**
   * Handles the line item removal.
   * Accepts either a line item key (string) or line index (number).
   * @param {string | number} keyOrLine - The line item key or index.
   * @param {Event} [event] - Optional triggering event.
   */
  onLineItemRemove(keyOrLine, event) {
    let key = typeof keyOrLine === 'string' ? keyOrLine : null;
    let line = typeof keyOrLine === 'number' ? keyOrLine : null;
    let cartItemRowToRemove = null;

    const allRows = this.#getAllRows();

    if (key) {
      cartItemRowToRemove = allRows.find((row) => row.dataset.key === key);
    } else if (line) {
      cartItemRowToRemove = allRows[line - 1];
      key = cartItemRowToRemove?.dataset?.key;
    }

    if (!cartItemRowToRemove && event?.target instanceof HTMLElement) {
      cartItemRowToRemove = event.target.closest('.cart-items__table-row');
      key = key || cartItemRowToRemove?.dataset?.key;
    }

    if (!key && cartItemRowToRemove?.dataset?.key) {
      key = cartItemRowToRemove.dataset.key;
    }

    if (!key && !cartItemRowToRemove) return;

    // Collect the row and all its nested/child rows (e.g. addons / bundle components)
    const rowsToRemove = cartItemRowToRemove
      ? [
          cartItemRowToRemove,
          ...allRows.filter((row) => row.dataset.parentKey && row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ]
      : [];

    const keysToRemove = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);
    if (key && !keysToRemove.includes(key)) {
      keysToRemove.push(key);
    }

    // Instantly remove / animate rows optimistically (0ms response time)
    rowsToRemove.forEach((row) => {
      if (row.classList.contains('removing')) return;
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      const remove = () => {
        row.remove();
      };

      if (prefersReducedMotion()) {
        remove();
      } else {
        onAnimationEnd(row, remove);
      }
    });

    // Queue updates in batch
    const updates = {};
    keysToRemove.forEach((k) => {
      updates[k] = 0;
    });

    this.queueUpdates(updates);
  }

  /**
   * Queues item updates and triggers debounced or queued network call
   * @param {Record<string, number>} updates - Object with line item keys and quantities
   */
  queueUpdates(updates) {
    Object.assign(this.#pendingUpdates, updates);

    // Shimmer cart total
    this.refs.cartTotal?.shimmer?.();

    // If a request is already in flight, pending updates will be processed once it completes
    if (this.#isRequestInFlight) {
      return;
    }

    // Short debounce (150ms) to batch multiple rapid delete clicks into a single network call
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null;
      this.#processPendingUpdates();
    }, 150);
  }

  /**
   * Sends the batched updates to Shopify Section Rendering API
   */
  async #processPendingUpdates() {
    if (this.#isRequestInFlight) return;

    const updatesToSend = { ...this.#pendingUpdates };
    const keys = Object.keys(updatesToSend);
    if (keys.length === 0) return;

    // Clear pending updates
    this.#pendingUpdates = {};
    this.#isRequestInFlight = true;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('update:user-action');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates: updatesToSend,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponse.errors) {
        this.#handleCartError(1, parsedResponse);
        return;
      }

      // If more updates were queued while this request was in flight, do NOT morph yet
      // to avoid resurrecting rows that the user just clicked to delete.
      if (Object.keys(this.#pendingUpdates).length > 0) {
        this.#isRequestInFlight = false;
        this.#processPendingUpdates();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponse.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount
        ? parseInt(newCartHiddenItemCount, 10)
        : parsedResponse.item_count ?? 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponse);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponse, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponse.sections,
        })
      );

      morphSection(this.sectionId, parsedResponse.sections[this.sectionId]);

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error('[CartItemsComponent]', error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#isRequestInFlight = false;
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

      // In case any updates were added right as the request finished
      if (Object.keys(this.#pendingUpdates).length > 0) {
        this.#processPendingUpdates();
      }
    }
  }

  /**
   * Updates the quantity (legacy/single call support).
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {string} [config.key] - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} [config.action] - The action.
   */
  updateQuantity(config) {
    const { key, line, quantity } = config;
    if (key) {
      this.queueUpdates({ [key]: quantity });
      return;
    }

    const allRows = this.#getAllRows();
    const row = line ? allRows[line - 1] : null;
    const rowKey = row?.dataset?.key;

    if (rowKey) {
      this.queueUpdates({ [rowKey]: quantity });
      return;
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action || 'change'}:user-action`);
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer?.();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line || 1, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : parsedResponseText.item_count ?? 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
